"use client";

import Link from "next/link";
import { Suspense, useMemo, useState } from "react";
import {
  LuUsers,
  LuStar,
  LuTrendingUp,
  LuFilter,
  LuX,
  LuSlidersHorizontal,
  LuSearchX,
  LuShieldCheck,
  LuBriefcase,
  LuMessageCircle,
} from "react-icons/lu";

import { useMentors } from "@/hooks/useMentors";
import { useAgencies, type Agency } from "@/hooks/useAgencies";
import { useRepresentatives, type Representative } from "@/hooks/useRepresentatives";
import {
  activeFilterCount as activeMentorFilterCount,
  type Mentor,
  type MentorFilters,
} from "@/hooks/useMentorFilters";
import {
  activeAgencyFilterCount,
  type AgencyFilters,
} from "@/hooks/useAgencyFilters";
import {
  activeRepresentativeFilterCount,
  type RepresentativeFilters,
} from "@/hooks/useRepresentativeFilters";

import { MentorCard, MentorSkeletonCard } from "./MentorCard";
import { AgencyCard, AgencySkeletonCard } from "./AgencyCard";
import { RepresentativeCard, RepresentativeSkeletonCard } from "./RepresentativeCard";
import { RepresentativeFilterPanel } from "./RepresentativeFilterPanel";
import { SearchBar } from "./SearchBar";
import { SortDropdown, type SortKey } from "./SortDropdown";
import { TypePillToggle, type BrowseType } from "./TypePillToggle";
import { MentorFilterPanel } from "./MentorFilterPanel";
import { AgencyFilterPanel } from "./AgencyFilterPanel";
import { useBrowseUrlState } from "./useBrowseUrlState";

// ── Types ────────────────────────────────────────────────────────────────────

/** A mentor as returned by the API may carry a few fields not in the
 *  formal `Mentor` type (which we can't modify per spec). We extend
 *  locally so search/sort can use them in a type-safe way. */
type MentorEx = Mentor & {
  expertise?: string[] | null;
  servicesOffered?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type Item =
  | { kind: "mentor"; data: MentorEx }
  | { kind: "agency"; data: Agency }
  | { kind: "representative"; data: Representative };

export type BrowseListProps = {
  defaultType: BrowseType;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function lower(s: string | null | undefined): string {
  return (s ?? "").toLowerCase();
}

function matchesQuery(item: Item, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  if (!needle) return true;

  if (item.kind === "mentor") {
    const m = item.data;
    const hayParts: string[] = [
      lower(m.displayName),
      lower(m.bio),
      ...(Array.isArray(m.countriesOfExpertise) ? m.countriesOfExpertise : []).map(lower),
      ...(Array.isArray(m.fieldsOfStudy) ? m.fieldsOfStudy : []).map(lower),
      ...(Array.isArray(m.expertise) ? m.expertise : []).map(lower),
      ...(Array.isArray(m.servicesOffered) ? m.servicesOffered : []).map(lower),
    ];
    return hayParts.some((s) => s.includes(needle));
  }
  if (item.kind === "representative") {
    const r = item.data;
    const hayParts: string[] = [
      lower(r.institutionName),
      lower(r.description),
      lower(r.country),
      lower(r.institutionType),
      lower(r.user.firstName),
      lower(r.user.lastName),
      ...(Array.isArray(r.fieldsOffered) ? r.fieldsOffered : []).map(lower),
      ...(Array.isArray(r.countriesAccepting) ? r.countriesAccepting : []).map(lower),
    ];
    return hayParts.some((s) => s.includes(needle));
  }
  {
    const a = item.data;
    const officeStrings: string[] = Array.isArray(a.officeLocations)
      ? a.officeLocations.flatMap((o) => [lower(o.city), lower(o.country)])
      : [];
    const hayParts: string[] = [
      lower(a.name),
      lower(a.description),
      ...(Array.isArray(a.countriesCovered) ? a.countriesCovered : []).map(lower),
      ...(Array.isArray(a.fieldsCovered) ? a.fieldsCovered : []).map(lower),
      ...(Array.isArray(a.servicesOffered) ? a.servicesOffered : []).map(lower),
      ...officeStrings,
    ];
    return hayParts.some((s) => s.includes(needle));
  }
}

function topRatedScore(rating: number, totalReviews: number): number {
  return rating * Math.log10(1 + totalReviews);
}

function itemRating(item: Item): number {
  if (item.kind === "representative") return 0;
  return item.data.rating ?? 0;
}

function itemReviews(item: Item): number {
  if (item.kind === "representative") return 0;
  return item.data.totalReviews ?? 0;
}

function itemPrice(item: Item): number | null {
  return item.kind === "mentor" ? item.data.hourlyRate ?? null : null;
}

function itemTimestamp(item: Item): number {
  if (item.kind === "mentor") {
    const m = item.data;
    return new Date(m.updatedAt ?? m.createdAt ?? 0).getTime() || 0;
  }
  if (item.kind === "representative") {
    const r = item.data;
    return new Date(r.updatedAt ?? r.createdAt ?? 0).getTime() || 0;
  }
  const a = item.data;
  return new Date(a.updatedAt ?? a.createdAt ?? 0).getTime() || 0;
}

function sortItems(items: Item[], sort: SortKey): Item[] {
  const copy = items.slice();
  switch (sort) {
    case "top":
      copy.sort(
        (a, b) =>
          topRatedScore(itemRating(b), itemReviews(b)) -
          topRatedScore(itemRating(a), itemReviews(a))
      );
      break;
    case "reviewed":
      copy.sort((a, b) => itemReviews(b) - itemReviews(a));
      break;
    case "price-asc":
      copy.sort((a, b) => {
        const pa = itemPrice(a);
        const pb = itemPrice(b);
        // Items without a price (i.e. agencies) sort to the bottom.
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return pa - pb;
      });
      break;
    case "price-desc":
      copy.sort((a, b) => {
        const pa = itemPrice(a);
        const pb = itemPrice(b);
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return pb - pa;
      });
      break;
    case "recent":
      copy.sort((a, b) => itemTimestamp(b) - itemTimestamp(a));
      break;
  }
  return copy;
}

// ── Filter application (mentor/agency-aware, used by All mode too) ──────────

function applyMentorFilters(
  mentors: MentorEx[],
  filters: MentorFilters
): MentorEx[] {
  return mentors.filter((m) => {
    if (filters.countries.length > 0) {
      const coe = Array.isArray(m.countriesOfExpertise)
        ? m.countriesOfExpertise
        : [];
      if (!coe.some((c) => filters.countries.includes(c))) return false;
    }
    if (filters.fields.length > 0) {
      const fos = Array.isArray(m.fieldsOfStudy) ? m.fieldsOfStudy : [];
      if (!fos.some((f) => filters.fields.includes(f))) return false;
    }
    if ((m.rating ?? 0) < filters.minRating) return false;
    if (filters.onlyAvailable && !m.isAvailableForHire) return false;
    if (m.hourlyRate == null) {
      if (filters.hideMentorsWithoutRate) return false;
    } else if (
      filters.maxHourlyRate !== null &&
      m.hourlyRate > filters.maxHourlyRate
    ) {
      return false;
    }
    if ((m.totalReviews ?? 0) < filters.minReviews) return false;
    return true;
  });
}

const TEAM_SIZE_RANGES: Record<string, [number, number]> = {
  "1-10": [1, 10],
  "11-50": [11, 50],
  "51-200": [51, 200],
  "200+": [201, Number.POSITIVE_INFINITY],
};

function applyRepresentativeFilters(
  reps: Representative[],
  filters: RepresentativeFilters
): Representative[] {
  return reps.filter((r) => {
    if (filters.countries.length > 0 && !filters.countries.includes(r.country)) {
      return false;
    }
    if (filters.fields.length > 0) {
      const rf = Array.isArray(r.fieldsOffered) ? r.fieldsOffered : [];
      if (!rf.some((f) => filters.fields.includes(f))) return false;
    }
    if (filters.institutionTypes.length > 0) {
      if (!r.institutionType || !filters.institutionTypes.includes(r.institutionType)) {
        return false;
      }
    }
    if (filters.services.length > 0) {
      const rs = Array.isArray(r.servicesOffered) ? r.servicesOffered : [];
      if (!rs.some((s) => filters.services.includes(s))) return false;
    }
    return true;
  });
}

function applyAgencyFilters(
  agencies: Agency[],
  filters: AgencyFilters
): Agency[] {
  return agencies.filter((a) => {
    if (filters.countries.length > 0) {
      const ac = Array.isArray(a.countriesCovered) ? a.countriesCovered : [];
      if (!ac.some((c) => filters.countries.includes(c))) return false;
    }
    if (filters.fields.length > 0) {
      const af = Array.isArray(a.fieldsCovered) ? a.fieldsCovered : [];
      if (!af.some((f) => filters.fields.includes(f))) return false;
    }
    if (filters.services.length > 0) {
      const as = Array.isArray(a.servicesOffered) ? a.servicesOffered : [];
      if (!as.some((s) => filters.services.includes(s))) return false;
    }
    if ((a.rating ?? 0) < filters.minRating) return false;
    if (filters.teamSizes.length > 0) {
      if (a.teamSize == null) return false;
      const ok = filters.teamSizes.some((b) => {
        const [lo, hi] = TEAM_SIZE_RANGES[b];
        return a.teamSize! >= lo && a.teamSize! <= hi;
      });
      if (!ok) return false;
    }
    return true;
  });
}

// ── Inner component (Suspense boundary required for useSearchParams) ────────

function BrowseListInner({ defaultType }: BrowseListProps) {
  const {
    state,
    setQ,
    setType,
    setSort,
    setMentorFilters,
    setAgencyFilters,
    setRepresentativeFilters,
    clearCurrentFilters,
  } = useBrowseUrlState(defaultType);

  const {
    mentors: rawMentors,
    leaderboard,
    loading: mentorsLoading,
    error: mentorsError,
    mutateMentors,
  } = useMentors();
  const { agencies: rawAgencies, loading: agenciesLoading } = useAgencies();
  const { representatives: rawRepresentatives, loading: repsLoading } = useRepresentatives();

  const mentors = useMemo(() => (rawMentors ?? []) as MentorEx[], [rawMentors]);
  const agencies = useMemo(() => (rawAgencies ?? []) as Agency[], [rawAgencies]);
  const representatives = useMemo(
    () => (rawRepresentatives ?? []) as Representative[],
    [rawRepresentatives],
  );

  // Decide which loading flags actually matter for the current view.
  const loading =
    state.type === "mentor"
      ? mentorsLoading
      : state.type === "agency"
      ? agenciesLoading
      : state.type === "representative"
      ? repsLoading
      : mentorsLoading || agenciesLoading || repsLoading;

  // Surface a fetch failure so it isn't rendered as "no results exist".
  const loadError =
    state.type === "mentor" || state.type === "all" ? mentorsError : undefined;

  // ── Derive distinct facets (countries / fields / services / maxRate) ─────

  const mentorFacets = useMemo(() => {
    const countrySet = new Set<string>();
    const fieldSet = new Set<string>();
    let rate = 500;
    for (const m of mentors) {
      if (Array.isArray(m.countriesOfExpertise)) {
        for (const c of m.countriesOfExpertise) if (c) countrySet.add(c);
      }
      if (Array.isArray(m.fieldsOfStudy)) {
        for (const f of m.fieldsOfStudy) if (f) fieldSet.add(f);
      }
      if (typeof m.hourlyRate === "number" && m.hourlyRate > rate) {
        rate = m.hourlyRate;
      }
    }
    return {
      countries: Array.from(countrySet).sort(),
      fields: Array.from(fieldSet).sort(),
      maxRate: rate,
    };
  }, [mentors]);

  const agencyFacets = useMemo(() => {
    const countrySet = new Set<string>();
    const fieldSet = new Set<string>();
    const serviceSet = new Set<string>();
    for (const a of agencies) {
      if (Array.isArray(a.countriesCovered)) {
        for (const c of a.countriesCovered) if (c) countrySet.add(c);
      }
      if (Array.isArray(a.fieldsCovered)) {
        for (const f of a.fieldsCovered) if (f) fieldSet.add(f);
      }
      if (Array.isArray(a.servicesOffered)) {
        for (const s of a.servicesOffered) if (s) serviceSet.add(s);
      }
    }
    return {
      countries: Array.from(countrySet).sort(),
      fields: Array.from(fieldSet).sort(),
      services: Array.from(serviceSet).sort(),
    };
  }, [agencies]);

  const repFacets = useMemo(() => {
    const countrySet = new Set<string>();
    const fieldSet = new Set<string>();
    const serviceSet = new Set<string>();
    for (const r of representatives) {
      if (r.country) countrySet.add(r.country);
      if (Array.isArray(r.fieldsOffered)) {
        for (const f of r.fieldsOffered) if (f) fieldSet.add(f);
      }
      if (Array.isArray(r.servicesOffered)) {
        for (const s of r.servicesOffered) if (s) serviceSet.add(s);
      }
    }
    return {
      countries: Array.from(countrySet).sort(),
      fields: Array.from(fieldSet).sort(),
      services: Array.from(serviceSet).sort(),
    };
  }, [representatives]);

  // For the "All" filter UI: union of countries.
  const sharedCountries = useMemo(() => {
    const set = new Set<string>([
      ...mentorFacets.countries,
      ...agencyFacets.countries,
    ]);
    return Array.from(set).sort();
  }, [mentorFacets.countries, agencyFacets.countries]);

  // ── Filtered + sorted items (memoized) ───────────────────────────────────

  const [mentorTab, setMentorTab] = useState<"hire" | "advice">("hire");

  // Reads isAvailableForHire regardless of whether the API returns camelCase or snake_case
  const getIsHire = (data: unknown): boolean | undefined => {
    const d = data as Record<string, unknown>;
    const v = d["isAvailableForHire"] ?? d["is_available_for_hire"];
    return v as boolean | undefined;
  };

  const filtered = useMemo<Item[]>(() => {
    const passesTab = (item: Item): boolean => {
      const avail = getIsHire(item.data);
      return mentorTab === "hire" ? avail === true : avail !== true;
    };

    if (state.type === "representative") {
      return applyRepresentativeFilters(representatives, state.representative)
        .map((r): Item => ({ kind: "representative", data: r }))
        .filter((i) => matchesQuery(i, state.q) && passesTab(i));
    }
    if (state.type === "mentor") {
      return applyMentorFilters(mentors, state.mentor)
        .map((m): Item => ({ kind: "mentor", data: m }))
        .filter((i) => matchesQuery(i, state.q) && passesTab(i));
    }
    if (state.type === "agency") {
      return applyAgencyFilters(agencies, state.agency)
        .map((a): Item => ({ kind: "agency", data: a }))
        .filter((i) => matchesQuery(i, state.q) && passesTab(i));
    }
    // "all" mode — apply only the shared filters.
    const filteredMentors = applyMentorFilters(mentors, {
      ...state.mentor,
      onlyAvailable: false,
      maxHourlyRate: null,
      hideMentorsWithoutRate: false,
      minReviews: 0,
      fields: [],
    });
    const filteredAgencies = applyAgencyFilters(agencies, {
      ...state.agency,
      services: [],
      teamSizes: [],
      fields: [],
    });
    const merged: Item[] = [
      ...filteredMentors.map((m): Item => ({ kind: "mentor", data: m })),
      ...filteredAgencies.map((a): Item => ({ kind: "agency", data: a })),
      ...representatives.map((r): Item => ({ kind: "representative", data: r })),
    ];
    return merged.filter((i) => matchesQuery(i, state.q) && passesTab(i));
  }, [state.type, state.q, state.mentor, state.agency, state.representative, mentors, agencies, representatives, mentorTab]);

  const sortedItems = useMemo(
    () => sortItems(filtered, state.sort),
    [filtered, state.sort]
  );

  // ── Counts for pill toggle ───────────────────────────────────────────────

  const typeCounts = useMemo(() => {
    if (loading) return undefined;
    // Counts are based on the search query AND the shared filters only,
    // so the pill counts feel intuitive ("how many would I see if I
    // switched modes right now?").
    const sharedMentor: MentorFilters = {
      countries: state.mentor.countries,
      fields: [],
      minRating: state.mentor.minRating,
      onlyAvailable: false,
      maxHourlyRate: null,
      hideMentorsWithoutRate: false,
      minReviews: 0,
    };
    const sharedAgency: AgencyFilters = {
      countries: state.agency.countries,
      fields: [],
      services: [],
      minRating: state.agency.minRating,
      teamSizes: [],
    };
    const m = applyMentorFilters(mentors, sharedMentor)
      .map((data): Item => ({ kind: "mentor", data }))
      .filter((i) => matchesQuery(i, state.q)).length;
    const a = applyAgencyFilters(agencies, sharedAgency)
      .map((data): Item => ({ kind: "agency", data }))
      .filter((i) => matchesQuery(i, state.q)).length;
    const r = representatives
      .map((data): Item => ({ kind: "representative", data }))
      .filter((i) => matchesQuery(i, state.q)).length;
    return { all: m + a + r, mentor: m, agency: a, representative: r };
  }, [
    loading,
    mentors,
    agencies,
    representatives,
    state.q,
    state.mentor.countries,
    state.mentor.minRating,
    state.agency.countries,
    state.agency.minRating,
  ]);

  // ── Active filter counts ─────────────────────────────────────────────────

  const activeCount = useMemo(() => {
    if (state.type === "mentor") {
      return activeMentorFilterCount(state.mentor, mentorFacets.maxRate);
    }
    if (state.type === "agency") {
      return activeAgencyFilterCount(state.agency);
    }
    if (state.type === "representative") {
      return activeRepresentativeFilterCount(state.representative);
    }
    // "all" — only count countries + minRating
    let n = 0;
    if (state.mentor.countries.length > 0) n += 1;
    if (state.mentor.minRating > 0) n += 1;
    return n;
  }, [state.type, state.mentor, state.agency, state.representative, mentorFacets.maxRate]);

  const tabCounts = useMemo(() => {
    const count = (items: Item[]) => ({
      hire: items.filter((i) => getIsHire(i.data) === true).length,
      advice: items.filter((i) => getIsHire(i.data) !== true).length,
    });
    if (state.type === "mentor") {
      return count(
        applyMentorFilters(mentors, state.mentor)
          .map((m): Item => ({ kind: "mentor", data: m }))
          .filter((i) => matchesQuery(i, state.q))
      );
    }
    if (state.type === "agency") {
      return count(
        applyAgencyFilters(agencies, state.agency)
          .map((a): Item => ({ kind: "agency", data: a }))
          .filter((i) => matchesQuery(i, state.q))
      );
    }
    if (state.type === "representative") {
      return count(
        applyRepresentativeFilters(representatives, state.representative)
          .map((r): Item => ({ kind: "representative", data: r }))
          .filter((i) => matchesQuery(i, state.q))
      );
    }
    // "all"
    const allItems: Item[] = [
      ...applyMentorFilters(mentors, { ...state.mentor, onlyAvailable: false, maxHourlyRate: null, hideMentorsWithoutRate: false, minReviews: 0, fields: [] })
        .map((m): Item => ({ kind: "mentor", data: m })),
      ...applyAgencyFilters(agencies, { ...state.agency, services: [], teamSizes: [], fields: [] })
        .map((a): Item => ({ kind: "agency", data: a })),
      ...representatives.map((r): Item => ({ kind: "representative", data: r })),
    ].filter((i) => matchesQuery(i, state.q));
    return count(allItems);
  }, [state.type, state.mentor, state.agency, state.representative, state.q, mentors, agencies, representatives]);

  // ── UI ───────────────────────────────────────────────────────────────────

  const [mobileOpen, setMobileOpen] = useState(false);

  // Hero copy: per-page, with reasonable defaults for "all".
  const heroCopy = useMemo(() => {
    if (defaultType === "agency") {
      return {
        title: (
          <>
            Find a Trusted Agency
            <br />
            <span className="text-indigo-200">for Your Study Abroad</span>
          </>
        ),
        description:
          "Browse approved education agencies. Compare services, coverage, and ratings to find the right partner for your application journey.",
      };
    }
    return {
      title: (
        <>
          Connect with Expert
          <br />
          <span className="text-indigo-200">Study Abroad Advisors</span>
        </>
      ),
      description:
        "Learn from those who have been through the journey. Our individual experts share real experiences to help you navigate studying abroad.",
    };
  }, [defaultType]);

  // Heading shown above the grid
  const headingLabel = useMemo(() => {
    if (state.type === "mentor") return "All Individual Experts";
    if (state.type === "agency") return "All Agencies";
    if (state.type === "representative") return "All Representatives";
    return "All Mentors, Agencies & Representatives";
  }, [state.type]);

  // Total count of source data (for the "X of Y" subtitle)
  const totalForType = useMemo(() => {
    if (state.type === "mentor") return mentors.length;
    if (state.type === "agency") return agencies.length;
    if (state.type === "representative") return representatives.length;
    return mentors.length + agencies.length + representatives.length;
  }, [state.type, mentors.length, agencies.length, representatives.length]);

  // Filter panel — chosen per type. Memoized to avoid recreating per render.
  const filterPanel = (
    <>
      {state.type === "mentor" && (
        <MentorFilterPanel
          countries={mentorFacets.countries}
          fields={mentorFacets.fields}
          maxRate={mentorFacets.maxRate}
          filters={state.mentor}
          setFilters={setMentorFilters}
          clear={clearCurrentFilters}
          activeCount={activeCount}
        />
      )}
      {state.type === "agency" && (
        <AgencyFilterPanel
          countries={agencyFacets.countries}
          fields={agencyFacets.fields}
          services={agencyFacets.services}
          filters={state.agency}
          setFilters={setAgencyFilters}
          clear={clearCurrentFilters}
          activeCount={activeCount}
        />
      )}
      {state.type === "representative" && (
        <RepresentativeFilterPanel
          countries={repFacets.countries}
          fields={repFacets.fields}
          services={repFacets.services}
          filters={state.representative}
          setFilters={setRepresentativeFilters}
          clear={clearCurrentFilters}
          activeCount={activeCount}
        />
      )}
      {state.type === "all" && (
        <SharedFilterPanel
          countries={sharedCountries}
          minRating={state.mentor.minRating}
          selectedCountries={state.mentor.countries}
          onToggleCountry={(c) =>
            setMentorFilters((prev) => ({
              ...prev,
              countries: prev.countries.includes(c)
                ? prev.countries.filter((x) => x !== c)
                : [...prev.countries, c],
            }))
          }
          onMinRatingChange={(v) => setMentorFilters({ minRating: v })}
          activeCount={activeCount}
          clear={clearCurrentFilters}
        />
      )}
    </>
  );

  // ── Top-rated leaderboard strip — only on mentor-default page ────────────

  const showMentorLeaderboard =
    defaultType === "mentor" &&
    state.type === "mentor" &&
    !mentorsLoading &&
    leaderboard &&
    leaderboard.length > 0;

  const agencyLeaderboard = useMemo(() => {
    if (defaultType !== "agency") return [];
    if (state.type !== "agency") return [];
    if (!agencies || agencies.length < 5) return [];
    return [...agencies]
      .sort((a, b) => {
        if ((b.rating ?? 0) !== (a.rating ?? 0)) {
          return (b.rating ?? 0) - (a.rating ?? 0);
        }
        return (b.totalReviews ?? 0) - (a.totalReviews ?? 0);
      })
      .slice(0, 5);
  }, [defaultType, state.type, agencies]);

  return (
    <div className="min-h-screen overflow-x-clip">
      {/* Hero */}
      <section className="relative overflow-hidden bg-linear-to-br from-indigo-600 via-indigo-700 to-purple-800 dark:from-indigo-900 dark:via-indigo-950 dark:to-purple-950">
        <div className="relative max-w-5xl mx-auto px-5 py-16 lg:py-20">
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {heroCopy.title}
          </h1>
          <p className="text-base lg:text-lg text-indigo-100 max-w-2xl leading-relaxed">
            {heroCopy.description}
          </p>
        </div>
      </section>

      {/* Mentor leaderboard strip */}
      {showMentorLeaderboard && leaderboard && (
        <section className="max-w-6xl mx-auto px-5 mt-8">
          <div className="flex items-center gap-2 mb-4">
            <LuTrendingUp className="w-4 h-4 text-amber-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Top Individual Experts
            </h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none">
            {leaderboard.slice(0, 5).map((mentor: Mentor, idx: number) => (
              <Link
                key={mentor.id}
                href={`/mentors/${mentor.id}`}
                className="flex-none flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-all"
              >
                <span
                  className={`text-sm font-bold ${
                    idx === 0
                      ? "text-amber-500"
                      : idx === 1
                      ? "text-gray-400"
                      : idx === 2
                      ? "text-orange-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  #{idx + 1}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {mentor.displayName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {mentor.totalPoints ?? 0} pts
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Agency leaderboard strip */}
      {agencyLeaderboard.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 mt-8">
          <div className="flex items-center gap-2 mb-4">
            <LuTrendingUp className="w-4 h-4 text-amber-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Top Agencies
            </h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none">
            {agencyLeaderboard.map((agency, idx) => (
              <Link
                key={agency.id}
                href={`/agencies/${agency.id}`}
                className="flex-none flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-all"
              >
                <span
                  className={`text-sm font-bold ${
                    idx === 0
                      ? "text-amber-500"
                      : idx === 1
                      ? "text-gray-400"
                      : idx === 2
                      ? "text-orange-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  #{idx + 1}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {agency.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  <LuStar className="w-3 h-3 text-amber-400 fill-amber-400" />
                  {(agency.rating ?? 0).toFixed(1)}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Main content with sidebar */}
      <section className="max-w-6xl mx-auto px-5 py-10 lg:py-14">
        {/* Heading + summary count */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
            {headingLabel}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm"
              aria-label="Open filters"
            >
              <LuFilter className="w-4 h-4" />
              Filters
              {activeCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-indigo-600 text-white text-xs w-5 h-5">
                  {activeCount}
                </span>
              )}
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {sortedItems.length} of {totalForType}{" "}
              {state.type === "mentor"
                ? totalForType === 1
                  ? "individual expert"
                  : "individual experts"
                : state.type === "agency"
                ? totalForType === 1
                  ? "agency"
                  : "agencies"
                : state.type === "representative"
                ? totalForType === 1
                  ? "representative"
                  : "representatives"
                : "results"}
            </span>
          </div>
        </div>

        {/* Toolbar: type pill row + search + sort row */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="overflow-x-auto pb-0.5 scrollbar-none">
            <TypePillToggle
              value={state.type}
              onChange={setType}
              counts={typeCounts}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <SearchBar value={state.q} onChange={setQ} />
            <SortDropdown
              value={state.sort}
              onChange={setSort}
              hidePriceSorts={state.type === "agency" || state.type === "representative"}
            />
          </div>
        </div>

        {/* Sub-tabs: Hire Expert / Get Advice */}
        <div className="flex gap-2 mb-4">
          {(["hire", "advice"] as const).map((tab) => {
            const active = mentorTab === tab;
            const count = tabCounts[tab];
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setMentorTab(tab)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-500/40"
                }`}
              >
                {tab === "hire" ? (
                  <LuBriefcase className="w-4 h-4" />
                ) : (
                  <LuMessageCircle className="w-4 h-4" />
                )}
                {tab === "hire" ? "Hire Expert" : "Get Advice"}
                {!loading && (
                  <span
                    className={`inline-flex items-center justify-center rounded-full text-xs w-5 h-5 ${
                      active
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeCount > 0 && (
          <div className="mb-4 flex items-center gap-3 px-3 py-2 rounded-lg bg-indigo-50/80 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 text-sm text-indigo-800 dark:text-indigo-200">
            <LuFilter className="w-4 h-4" />
            <span>
              {activeCount} filter{activeCount !== 1 ? "s" : ""} active
            </span>
            <span className="text-indigo-400">·</span>
            <button
              onClick={clearCurrentFilters}
              className="font-medium hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block sticky top-24 h-fit p-5 rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10">
            {filterPanel}
          </aside>

          {/* Mobile drawer */}
          {mobileOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div
                className="flex-1 bg-black/50"
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
              />
              <div className="w-80 max-w-[85vw] bg-white dark:bg-gray-900 p-5 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Filters
                  </h3>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/5"
                    aria-label="Close filters"
                  >
                    <LuX className="w-5 h-5" />
                  </button>
                </div>
                {filterPanel}
                <button
                  onClick={() => setMobileOpen(false)}
                  className="mt-6 w-full py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                >
                  Show {sortedItems.length} result
                  {sortedItems.length === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          )}

          {/* Grid */}
          <div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                  state.type === "agency" ? (
                    <AgencySkeletonCard key={i} />
                  ) : state.type === "representative" ? (
                    <RepresentativeSkeletonCard key={i} />
                  ) : (
                    <MentorSkeletonCard key={i} />
                  )
                )}
              </div>
            ) : loadError && totalForType === 0 ? (
              <div className="text-center py-16 text-gray-500 dark:text-gray-400 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
                <LuSearchX className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Couldn&apos;t load this list.</p>
                <p className="text-sm mt-1">
                  Check your connection and try again.
                </p>
                <button
                  onClick={() => mutateMentors()}
                  className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  Retry
                </button>
              </div>
            ) : totalForType === 0 ? (
              <EmptyDataset type={state.type} />
            ) : sortedItems.length === 0 ? (
              <div className="text-center py-16 text-gray-500 dark:text-gray-400 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
                <LuSearchX className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">No matches.</p>
                <p className="text-sm mt-1">
                  Try clearing some filters.
                </p>
                <button
                  onClick={clearCurrentFilters}
                  className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {sortedItems.map((item) =>
                  item.kind === "mentor" ? (
                    <MentorCard
                      key={`mentor-${item.data.id}`}
                      mentor={item.data}
                      showTypeBadge={state.type === "all"}
                    />
                  ) : item.kind === "representative" ? (
                    <RepresentativeCard
                      key={`rep-${item.data.id}`}
                      representative={item.data}
                      showTypeBadge={state.type === "all"}
                    />
                  ) : (
                    <AgencyCard
                      key={`agency-${item.data.id}`}
                      agency={item.data}
                      showTypeBadge={state.type === "all"}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Country display helper ───────────────────────────────────────────────────

const COUNTRY_LABEL: Record<string, string> = { "United States": "USA" };
const countryLabel = (c: string) => COUNTRY_LABEL[c] ?? c;

const SHARED_LIMIT = 5;

function SharedCountryList({
  countries,
  selected,
  onToggle,
}: {
  countries: string[];
  selected: string[];
  onToggle: (c: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? countries : countries.slice(0, SHARED_LIMIT);
  const overflow = countries.length - SHARED_LIMIT;

  if (countries.length === 0) {
    return <p className="text-xs text-gray-400">No countries in dataset</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {visible.map((c) => (
        <label
          key={c}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={selected.includes(c)}
            onChange={() => onToggle(c)}
            className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="truncate">{countryLabel(c)}</span>
        </label>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-0.5 text-left text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {expanded ? "Show less" : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}

// ── Shared (All-mode) filter panel — only countries + minRating ─────────────

function SharedFilterPanel({
  countries,
  selectedCountries,
  onToggleCountry,
  minRating,
  onMinRatingChange,
  activeCount,
  clear,
}: {
  countries: string[];
  selectedCountries: string[];
  onToggleCountry: (c: string) => void;
  minRating: number;
  onMinRatingChange: (v: number) => void;
  activeCount: number;
  clear: () => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LuSlidersHorizontal className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm uppercase tracking-wide">
            Filters
          </h3>
        </div>
        {activeCount > 0 && (
          <button
            onClick={clear}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Country
        </h4>
        <SharedCountryList
          countries={countries}
          selected={selectedCountries}
          onToggle={onToggleCountry}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Minimum rating
          </h4>
          <span className="text-xs text-gray-500">{minRating.toFixed(1)}+</span>
        </div>
        <input
          type="range"
          min={0}
          max={5}
          step={0.1}
          value={minRating}
          onChange={(e) => onMinRatingChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-600"
          aria-label="Minimum rating"
        />
      </div>
    </div>
  );
}

// ── Empty-state when there's literally nothing in the source data ───────────

function EmptyDataset({ type }: { type: BrowseType }) {
  if (type === "agency") {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
        <LuShieldCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-lg font-medium">No agencies yet</p>
        <p className="text-sm mt-1">No approved agencies are listed yet.</p>
      </div>
    );
  }
  if (type === "representative") {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
        <LuUsers className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-lg font-medium">No representatives yet</p>
        <p className="text-sm mt-1">No approved representatives are listed yet.</p>
      </div>
    );
  }
  if (type === "mentor") {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400">
        <LuUsers className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-lg font-medium">No individual experts yet</p>
        <p className="text-sm mt-1">Be the first to apply as an individual expert!</p>
      </div>
    );
  }
  return (
    <div className="text-center py-16 text-gray-500 dark:text-gray-400 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
      <LuSearchX className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p className="text-lg font-medium">No results yet</p>
      <p className="text-sm mt-1">There are no mentors or agencies to display.</p>
    </div>
  );
}

// ── Outer export wraps in Suspense (Next.js requires it for
// useSearchParams in client components rendered at /page entry). ─────────────

export function BrowseList(props: BrowseListProps) {
  return (
    <Suspense fallback={<BrowseListFallback type={props.defaultType} />}>
      <BrowseListInner {...props} />
    </Suspense>
  );
}

function BrowseListFallback({ type }: { type: BrowseType }) {
  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-linear-to-br from-indigo-600 via-indigo-700 to-purple-800 dark:from-indigo-900 dark:via-indigo-950 dark:to-purple-950">
        <div className="relative max-w-5xl mx-auto px-5 py-16 lg:py-20" />
      </section>
      <section className="max-w-6xl mx-auto px-5 py-10 lg:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) =>
            type === "agency" ? (
              <AgencySkeletonCard key={i} />
            ) : (
              <MentorSkeletonCard key={i} />
            )
          )}
        </div>
      </section>
    </div>
  );
}
